import { useCallback, useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { checkPushSupport, getCurrentSubscription, subscribePush, unsubscribePush } from "../../lib/push";
import { disableNativePush, enableNativePush, readStoredPushToken } from "../../lib/native/nativePush";

const isNative = (): boolean => {
  const support = checkPushSupport();
  return support.supported && support.transport === "apns";
};

type State = "loading" | "enabled" | "disabled" | "unsupported";

export default function NudgesPanel() {
  const [state, setState] = useState<State>("loading");
  const [busy, setBusy] = useState(false);
  const [unsupportedReason, setUnsupportedReason] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const support = checkPushSupport();
    if (!support.supported) {
      setUnsupportedReason(support.reason);
      setState("unsupported");
      return;
    }
    if (support.transport === "apns") {
      setState(readStoredPushToken() ? "enabled" : "disabled");
      return;
    }
    const sub = await getCurrentSubscription();
    setState(sub ? "enabled" : "disabled");
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleEnable = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isNative()) {
        const permission = await enableNativePush();
        if (permission !== "granted") {
          throw new Error("Notifications are turned off for Brain Dump. Enable them in Settings → Notifications.");
        }
      } else {
        await subscribePush();
      }
      setState("enabled");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to enable nudges");
    }
    setBusy(false);
  };

  const handleDisable = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isNative()) {
        await disableNativePush();
      } else {
        await unsubscribePush();
      }
      setState("disabled");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disable nudges");
    }
    setBusy(false);
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5 text-muted-foreground" />
        <h3 className="text-sm font-medium text-foreground">Nudges</h3>
      </div>
      <p className="text-xs text-muted-foreground">
        Get pestered twice a day about tasks that have been sitting in your backlog.
      </p>

      {state === "loading" ? (
        <div className="text-xs text-muted-foreground">Checking…</div>
      ) : state === "unsupported" ? (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">
            Push notifications aren't available on this device.
          </div>
          {unsupportedReason === "iOS requires Add to Home Screen" ? (
            <div className="text-xs text-muted-foreground">
              On iPhone: open Brain Dump in Safari → tap <b>Share</b> → <b>Add to Home Screen</b>,
              then open the installed app and enable nudges here.
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">{unsupportedReason}</div>
          )}
        </div>
      ) : state === "enabled" ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-success" />
            <span className="text-sm text-foreground">Enabled on this device</span>
          </div>
          <button
            onClick={handleDisable}
            disabled={busy}
            className="text-xs text-destructive hover:text-destructive/80 transition-colors disabled:opacity-50"
          >
            {busy ? "Disabling…" : "Disable"}
          </button>
        </div>
      ) : (
        <button
          onClick={handleEnable}
          disabled={busy}
          className="btn-secondary flex items-center gap-2 text-sm disabled:opacity-50"
        >
          <Bell className="w-4 h-4" />
          {busy ? "Enabling…" : "Enable nudges"}
        </button>
      )}

      {error && <div className="text-xs text-destructive">{error}</div>}
    </div>
  );
}
